import type { BookingConfirmationHotel } from '@massilia/queries'

import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Calendar, ExternalLink, Hotel, MapPin } from 'lucide-react'

import { centsToEuros, formatPrice } from '@/utils/format-price'

import { pensionTypeLabels } from './pension-type-labels'

interface HotelsSectionProps {
  hotels: BookingConfirmationHotel[]
}

export function HotelsSection({ hotels }: HotelsSectionProps) {
  if (hotels.length === 0) return null

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-4 md:p-6">
      <div className="text-lg leading-7 font-semibold text-gray-light-900 sm:text-2xl">
        Hébergements
      </div>

      {hotels.map((hotel, i) => (
        <div
          key={i}
          className="flex flex-col gap-3 rounded-xl border border-gray-light-200 p-3 md:p-4"
        >
          {/* Hotel name + location */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-amber-50">
                <Hotel className="size-5 text-amber-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-light-900">{hotel.name}</span>
                <span className="flex items-center gap-1 text-xs font-medium text-gray-light-600">
                  <MapPin className="size-3" />
                  {hotel.location}
                </span>
              </div>
            </div>
            {hotel.hotelUrl && (
              <a
                className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
                href={hotel.hotelUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                Site <ExternalLink className="size-3" />
              </a>
            )}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-gray-light-500 uppercase">
                <Calendar className="size-3" /> Check-in
              </span>
              <span className="text-gray-light-800 text-sm font-semibold">
                {formatDate(hotel.checkIn)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-gray-light-500 uppercase">
                <Calendar className="size-3" /> Check-out
              </span>
              <span className="text-gray-light-800 text-sm font-semibold">
                {formatDate(hotel.checkOut)}
              </span>
            </div>
            {hotel.roomType && (
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-wider text-gray-light-500 uppercase">
                  Chambre
                </span>
                <span className="text-gray-light-800 text-sm font-semibold">{hotel.roomType}</span>
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold tracking-wider text-gray-light-500 uppercase">
                Prix
              </span>
              <span className="text-sm font-bold text-gray-light-900">
                {formatPrice(centsToEuros(hotel.price))}
              </span>
            </div>
          </div>

          {/* Pension type badge */}
          {hotel.pensionType && (
            <div>
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                {pensionTypeLabels.get(hotel.pensionType) ?? hotel.pensionType}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function formatDate(isoDate: string): string {
  const date = parseISO(isoDate)
  return format(date, 'dd MMM yyyy', { locale: fr })
}
